import { useState, useCallback } from 'react';
import api from '../lib/api';

/**
 * Hook for auto-filling medical records using AI extraction
 * Supports: vital signs, physical exam, mental health, multiple diagnoses with CIE-10
 *
 * @returns {Object} Hook state and functions
 */
export function useMedicalRecordAutoFill() {
  const [isExtracting, setIsExtracting] = useState(false);
  const [extractedData, setExtractedData] = useState(null);
  const [error, setError] = useState(null);

  /**
   * Extract medical data from transcription using GPT-4o-mini
   * @param {string} transcription - The transcribed consultation text
   * @returns {Promise<Object|null>} Extracted data or null on error
   */
  const extractMedicalData = useCallback(async (transcription) => {
    if (!transcription || transcription.trim() === '') {
      setError('No hay transcripción para analizar');
      return null;
    }

    setIsExtracting(true);
    setError(null);

    try {
      const response = await api.post('/ai/auto-fill-medical-record', {
        transcription: transcription
      });

      if (response.data.success) {
        setExtractedData(response.data.extracted_data);
        return response.data.extracted_data;
      } else {
        setError(response.data.error || 'Error al extraer datos');
        return null;
      }
    } catch (err) {
      const errorMessage = err.response?.data?.detail || err.message || 'Error al extraer datos clínicos';
      setError(errorMessage);
      console.error('Auto-fill error:', err);
      return null;
    } finally {
      setIsExtracting(false);
    }
  }, []);

  /**
   * Map extracted data to form fields
   * This function helps map the AI-extracted data to the consultation form structure
   * Now includes: vital signs, physical exam, mental health, and multiple diagnoses
   *
   * @param {Object} data - Extracted medical data
   * @param {Object} currentFormData - Current form data state
   * @param {Object} currentTreatment - Current treatment state
   * @returns {Object} Object with all mapped data
   */
  const mapToFormFields = useCallback((data, currentFormData = {}, currentTreatment = {}) => {
    if (!data) return { formData: currentFormData, treatment: currentTreatment };

    // Build reason from multiple fields
    const reasonParts = [];
    if (data.motivo_consulta) reasonParts.push(data.motivo_consulta);

    // Build clinical evaluation
    const evalParts = [];
    if (data.clinical_evaluation) evalParts.push(data.clinical_evaluation);
    if (data.sintomas_principales) evalParts.push(`Síntomas: ${data.sintomas_principales}`);
    if (data.antecedentes) evalParts.push(`Antecedentes: ${data.antecedentes}`);

    // Map to form data (tiempo_enfermedad now goes to its own field)
    const newFormData = {
      ...currentFormData,
      reason: data.motivo_consulta || currentFormData.reason || '',
      clinical_evaluation: evalParts.join('\n\n') || currentFormData.clinical_evaluation || '',
      tiempo_enfermedad: data.tiempo_enfermedad || currentFormData.tiempo_enfermedad || '',
      relato_enfermedad: data.relato_enfermedad || currentFormData.relato_enfermedad || '',
      funciones_psicologicas: data.funciones_psicologicas || currentFormData.funciones_psicologicas || '',
    };

    // Map vital signs
    const vitalSigns = data.vital_signs ? {
      blood_pressure: data.vital_signs.blood_pressure || '',
      heart_rate: data.vital_signs.heart_rate || '',
      temperature: data.vital_signs.temperature || '',
      respiratory_rate: data.vital_signs.respiratory_rate || '',
      oxygen_saturation: data.vital_signs.oxygen_saturation || '',
      weight: data.vital_signs.weight || '',
      height: data.vital_signs.height || ''
    } : null;

    // Map physical exam
    const physicalExam = data.physical_exam ? {
      cabeza: data.physical_exam.cabeza || null,
      cuello: data.physical_exam.cuello || null,
      cardiovascular: data.physical_exam.cardiovascular || null,
      respiratorio: data.physical_exam.respiratorio || null,
      piel: data.physical_exam.piel || null,
      abdomen: data.physical_exam.abdomen || null,
      genitourinario: data.physical_exam.genitourinario || null
    } : null;

    // Map mental health screening
    const mentalHealth = data.mental_health ? {
      q1: data.mental_health.q1_triste_deprimido,
      q2: data.mental_health.q2_perdio_interes,
      q3: data.mental_health.q3_problemas_sueno,
      q4: data.mental_health.q4_ansioso_nervioso,
      q5: data.mental_health.q5_pensamientos_negativos
    } : null;

    // Map multiple diagnoses
    const diagnoses = data.diagnoses?.map((d, index) => ({
      diagnosis: d.diagnosis || '',
      cie10_code: d.cie10_code || '',
      is_primary: d.is_primary || index === 0
    })) || [];

    // Ensure at least one diagnosis is primary
    if (diagnoses.length > 0 && !diagnoses.some(d => d.is_primary)) {
      diagnoses[0].is_primary = true;
    }

    // Map medications (expanded DIGEMID format)
    const medicamentos = data.medicamentos?.map(med => ({
      nombre: med.nombre || '',
      concentracion: med.concentracion || '',
      forma_farmaceutica: med.forma_farmaceutica?.toLowerCase() || 'tableta',
      cantidad: med.cantidad || '',
      dosis: med.dosis || '',
      frecuencia: med.frecuencia || '',
      duracion: med.duracion || '',
      via: med.via?.toLowerCase() || 'oral',
      indicaciones: med.indicaciones || ''
    })) || currentTreatment.medicamentos || [];

    // Map treatment
    const newTreatment = {
      ...currentTreatment,
      medicamentos: medicamentos.length > 0 ? medicamentos : currentTreatment.medicamentos || [],
      nutricion: data.nutricion || currentTreatment.nutricion || '',
      laboratorio: data.laboratorio || currentTreatment.laboratorio || [],
      imagenes: data.imagenes || currentTreatment.imagenes || [],
      interconsultas: data.interconsultas || currentTreatment.interconsultas || [],
      procedimientos: data.procedimientos || currentTreatment.procedimientos || []
    };

    return {
      formData: newFormData,
      treatment: newTreatment,
      vitalSigns,
      physicalExam,
      mentalHealth,
      diagnoses: diagnoses.length > 0 ? diagnoses : [{ diagnosis: '', cie10_code: '', is_primary: true }],
      indications: data.indications || ''
    };
  }, []);

  /**
   * Clear extracted data
   */
  const clearExtractedData = useCallback(() => {
    setExtractedData(null);
    setError(null);
  }, []);

  return {
    isExtracting,
    extractedData,
    error,
    extractMedicalData,
    mapToFormFields,
    clearExtractedData
  };
}

export default useMedicalRecordAutoFill;
